import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SipProvider } from "@/contexts/SipContext";
import { PERMISSIONS_MATRIX, hasRoleAccess } from "@/config/permissions";
import IncomingCallModal from "@/components/IncomingCallModal";
import TestBanner from "@/components/TestBanner";
import Landing from "@/pages/Landing";
import Features from "@/pages/Features";
import Pricing from "@/pages/Pricing";
import Subscribe from "@/pages/Subscribe";
import Onboarding from "@/pages/Onboarding";
import Dialer from "@/pages/Dialer";
import About from "@/pages/About";
import Help from "@/pages/Help";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import CallHistory from "@/pages/CallHistory";
import Analytics from "@/pages/Analytics";
import Reports from "@/pages/Reports";
import Contacts from "@/pages/Contacts";
import QueueMonitor from "@/pages/QueueMonitor";
import QueueMonitorTest from "@/pages/QueueMonitorTest";
import Profile from "@/pages/Profile";
import Support from "@/pages/Support";
import PlanManagement from "@/pages/PlanManagement";
import CompanySettings from "@/pages/CompanySettings";
import MyCalls from "@/pages/MyCalls";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Partners from "@/pages/Partners";
import Business from "@/pages/Business";
// Partner Portal imports
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerLayout from "@/pages/PartnerLayout";
import PartnerDashboard from "@/pages/PartnerDashboard";
import PartnerCustomers from "@/pages/PartnerCustomers";
import PartnerCustomerManagement from "@/pages/PartnerCustomerManagement";
import PartnerBilling from "@/pages/PartnerBilling";
import PartnerSettings from "@/pages/PartnerSettings";
// Admin Portal imports
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCompanies from "@/pages/AdminCompanies";
import AdminPartners from "@/pages/AdminPartners";
import AdminPricing from "@/pages/AdminPricing";
import AdminPermissions from "@/pages/AdminPermissions";
import AdminIntegrations from "@/pages/AdminIntegrations";
import AdminContent from "@/pages/AdminContent";
import AdminMaintenance from "@/pages/AdminMaintenance";
import AdminSettings from "@/pages/AdminSettings";
import AdminColors from "@/pages/AdminColors";
import AdminUsers from "@/pages/AdminUsers";
import DebugCallData from "@/pages/DebugCallData";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { registerServiceWorker } from "@/hooks/usePushNotifications";

// Global error handler for SIP.js internal errors
// These are non-fatal and can be safely ignored
const setupGlobalErrorHandler = () => {
  // Check if error message contains SIP.js state transition error
  const isSipStateError = (msg) => {
    if (!msg) return false;
    const msgStr = typeof msg === 'string' ? msg : msg.toString();
    return msgStr.includes('Invalid state transition');
  };

  // Override window.onerror
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (isSipStateError(message) || isSipStateError(error?.message)) {
      console.warn('Suppressed SIP.js state transition error');
      return true;
    }
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (isSipStateError(event.reason?.message)) {
      console.warn('Suppressed SIP.js promise rejection');
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });

  // Handle error events (catches React overlay errors)
  window.addEventListener('error', (event) => {
    if (isSipStateError(event.message) || isSipStateError(event.error?.message)) {
      console.warn('Suppressed SIP.js error event');
      event.preventDefault();
      event.stopImmediatePropagation();
      return true;
    }
  }, true); // Use capture phase
};

// Protected route wrapper - uses centralized PERMISSIONS_MATRIX
function ProtectedRoute({ children, requiredRole, requiredFeature }) {
  const { isAuthenticated, loading, user, hasFeatureAccess, permissionsLoaded } = useAuth();

  if (loading || (isAuthenticated && !permissionsLoaded)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Feature-based access control (from backend permissions)
  if (requiredFeature && !hasFeatureAccess(requiredFeature)) {
    return <Navigate to="/app/dialer" replace />;
  }

  // Role-based access control (legacy - for specific admin routes)
  if (requiredRole && requiredRole !== 'all') {
    const userRole = user?.role || 'standard';
    if (!hasRoleAccess(userRole, requiredRole)) {
      return <Navigate to="/app/dialer" replace />;
    }
  }

  return children;
}

// Component to handle default landing page redirect
function DefaultRedirect() {
  const defaultPage = localStorage.getItem('defaultLandingPage') || 'dialer';
  return <Navigate to={`/app/${defaultPage === 'dialer' ? 'dialer' : defaultPage}`} replace />;
}

// Wrapper for public pages with test banner
function PublicPage({ children }) {
  return (
    <>
      <TestBanner />
      {children}
    </>
  );
}

function AppRoutes() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes with test banner */}
      <Route path="/" element={<PublicPage><Landing /></PublicPage>} />
      <Route path="/features" element={<PublicPage><Features /></PublicPage>} />
      <Route path="/pricing" element={<PublicPage><Pricing /></PublicPage>} />
      <Route path="/partners" element={<PublicPage><Partners /></PublicPage>} />
      <Route path="/business" element={<PublicPage><Business /></PublicPage>} />
      <Route path="/login" element={<PublicPage><Login /></PublicPage>} />
      <Route path="/subscribe" element={<PublicPage><Subscribe /></PublicPage>} />
      <Route path="/onboarding" element={<PublicPage><Onboarding /></PublicPage>} />
      <Route path="/privacy" element={<PublicPage><Privacy /></PublicPage>} />
      <Route path="/terms" element={<PublicPage><Terms /></PublicPage>} />
      <Route path="/about" element={<PublicPage><About /></PublicPage>} />
      
      {/* Protected routes - App Dashboard */}
      <Route path="/app" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<DefaultRedirect />} />
        <Route path="dialer" element={<Dialer />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="queues" element={<QueueMonitorTest />} />
        <Route path="my-calls" element={<MyCalls />} />
        <Route path="history" element={<CallHistory />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="reports" element={<Reports />} />
        <Route path="company" element={<CompanySettings />} />
        <Route path="profile" element={<Profile />} />
        <Route path="support" element={<Support />} />
        <Route path="plan" element={
          <ProtectedRoute requiredRole="customer_admin">
            <PlanManagement />
          </ProtectedRoute>
        } />
        <Route path="about" element={<About />} />
        <Route path="help" element={<Help />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms" element={<Terms />} />
        <Route path="debug" element={<DebugCallData />} />
      </Route>

      {/* Legacy routes - redirect to new /app paths */}
      <Route path="/dialer" element={<Navigate to="/app/dialer" replace />} />
      <Route path="/contacts" element={<Navigate to="/app/contacts" replace />} />
      <Route path="/queues" element={<Navigate to="/app/queues" replace />} />
      <Route path="/my-calls" element={<Navigate to="/app/my-calls" replace />} />
      <Route path="/history" element={<Navigate to="/app/history" replace />} />
      <Route path="/analytics" element={<Navigate to="/app/analytics" replace />} />
      <Route path="/company" element={<Navigate to="/app/company" replace />} />
      <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
      <Route path="/developers" element={<Navigate to="/admin" replace />} />

      {/* Admin Portal routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="companies" element={<AdminCompanies />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="permissions" element={<AdminPermissions />} />
        <Route path="integrations" element={<AdminIntegrations />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="colors" element={<AdminColors />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="maintenance" element={<AdminMaintenance />} />
        <Route path="users" element={<AdminUsers />} />
      </Route>

      {/* Partner Portal routes */}
      <Route path="/partner/login" element={<PartnerLogin />} />
      <Route path="/partner" element={<PartnerLayout />}>
        <Route index element={<Navigate to="/partner/dashboard" replace />} />
        <Route path="dashboard" element={<PartnerDashboard />} />
        <Route path="customers" element={<PartnerCustomers />} />
        <Route path="customers/:customerId" element={<PartnerCustomerManagement />} />
        <Route path="billing" element={<PartnerBilling />} />
        <Route path="earnings" element={<Navigate to="/partner/billing" replace />} />
        <Route path="settings" element={<PartnerSettings />} />
        <Route path="support" element={<Support />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Setup global error handler on mount
  useEffect(() => {
    setupGlobalErrorHandler();
  }, []);

  // Register service worker for PWA
  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Auto-register preview environment with production for webhook forwarding
  useEffect(() => {
    const autoRegisterWithProduction = async () => {
      const currentHost = window.location.hostname;
      
      // Only auto-register if this is a preview environment (not production)
      if (currentHost.includes('connect-us.co.nz') && !currentHost.includes('preview')) {
        console.log('Production environment - no relay registration needed');
        return;
      }
      
      // Skip localhost for now
      if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
        console.log('Localhost - skipping relay registration');
        return;
      }
      
      try {
        // Call production's auto-register endpoint
        const response = await fetch('https://www.connect-us.co.nz/api/webhooks/relay/auto-register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          // The production server will read the Origin header to determine our URL
        });
        
        const data = await response.json();
        if (data.status === 'ok') {
          console.log('Webhook relay registered with production:', data.relay_url || data.message);
        } else {
          console.log('Relay registration skipped:', data.message);
        }
      } catch (error) {
        // Silently fail - this is a nice-to-have feature
        console.log('Could not register with production (may be offline or CORS):', error.message);
      }
    };
    
    // Run after a short delay to not block app startup
    const timer = setTimeout(autoRegisterWithProduction, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <SipProvider>
            <AppRoutes />
            <IncomingCallModal />
          </SipProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
